import React, { useState, useEffect } from 'react';
import { Modal, Tab, Tabs } from 'react-bootstrap';
import LineaTiempoAprobacion from './LineaTiempoAprobacion';
import NotasSolicitud from './NotasSolicitud';
import Swal from 'sweetalert2';
import moment from 'moment';
import { aprobarSolicitud, getDataList, rechazarSolicitud } from '../../api/common';

const ModalDetalleSolicitud = ({ solicitud: solicitudInicial, usuarioActual, show, onHide, onActualizar, onEditar }) => {
  const [solicitud, setSolicitud] = useState(solicitudInicial);
  const [loading, setLoading] = useState(false);
  const [procesando, setProcesando] = useState(false);

  useEffect(() => {
    if (show && solicitudInicial._id) {
      cargarDetalle();
    }
  }, [show, solicitudInicial._id]);

  const cargarDetalle = async () => {
    setLoading(true);
    try {
      const resultado = await getDataList('Registro', { conditions: { _id: solicitudInicial._id } });
      if (resultado.ok) {
        setSolicitud(resultado.data);
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
    } finally {
      setLoading(false);
    }
  };

  const puedeAprobar = () => {
    if (!solicitud.responsablesAprobacion) return false;
    const responsable = solicitud.responsablesAprobacion.find(
      r => r.usuario._id === usuarioActual._id && r.estado === 'pendiente'
    );
    return !!responsable && solicitud.estado !== 'rechazado' && solicitud.estado !== 'cancelado';
  };

  // El creador puede editar su solicitud mientras esté en estado pendiente.
  const puedeEditar = () => {
    const solicitanteId = solicitud?.solicitante?._id || solicitud?.solicitante;
    return String(solicitanteId) === String(usuarioActual?._id) &&
           solicitud?.estado === 'pendiente';
  };

  const handleAprobar = async () => {
    const result = await Swal.fire({
      title: '¿Aprobar solicitud?',
      html: `
        <textarea 
          id="comentarios-aprobacion" 
          class="form-control mt-3" 
          placeholder="Comentarios (opcional)" 
          rows="3"
        ></textarea>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      confirmButtonText: 'Aprobar',
      preConfirm: () => document.getElementById('comentarios-aprobacion').value
    });

    if (result.isConfirmed) {
      setProcesando(true);
      try {
        const resultado = await aprobarSolicitud(solicitud._id,{ usuarioId: usuarioActual._id, comentarios: result.value || '' });
        if (resultado.ok) {
          Swal.fire('¡Aprobado!', 'La solicitud ha sido aprobada', 'success');
          onActualizar();
          onHide();
        }
      } catch (error) {
        Swal.fire('Error', 'No se pudo aprobar la solicitud', 'error');
      } finally {
        setProcesando(false);
      }
    }
  };

  const handleRechazar = async () => {
    const result = await Swal.fire({
      title: '¿Rechazar solicitud?',
      html: `
        <textarea 
          id="comentarios-rechazo" 
          class="form-control mt-3" 
          placeholder="Motivo del rechazo (requerido)" 
          rows="3"
          required
        ></textarea>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Rechazar',
      preConfirm: () => {
        const comentarios = document.getElementById('comentarios-rechazo').value;
        if (!comentarios.trim()) {
          Swal.showValidationMessage('Debes indicar el motivo del rechazo');
        }
        return comentarios;
      }
    });

    if (result.isConfirmed) {
      setProcesando(true);
      try {
        const resultado = await rechazarSolicitud(solicitud._id, { usuarioId: usuarioActual._id, comentarios: result.value });
        if (resultado.ok) {
          Swal.fire('Rechazado', 'La solicitud ha sido rechazada', 'info');
          onActualizar();
          onHide();
        }
      } catch (error) {
        Swal.fire('Error', 'No se pudo rechazar la solicitud', 'error');
      } finally {
        setProcesando(false);
      }
    }
  };

  const getTipoInfo = (tipo) => {
    const tipos = {
      'permiso': { icon: 'calendar-check', color: 'warning' },
      'vacaciones': { icon: 'sun', color: 'info' },
      'capacitacion': { icon: 'mortarboard', color: 'danger' },
      'incapacidad': { icon: 'hospital', color: 'orange' },
      'insumo': { icon: 'box', color: 'purple' },
      'dotacion': { icon: 'shop', color: 'teal' },
      'compensatorio': { icon: 'clock', color: 'cyan' },
      'teletrabajo': { icon: 'house-door', color: 'success' },
      'reembolso': { icon: 'cash-coin', color: 'dark' },
      'equipamiento': { icon: 'laptop', color: 'secondary' },
      'mantenimiento': { icon: 'tools', color: 'brown' },
      'horasExtras': { icon: 'alarm', color: 'warning' },
      'otro': { icon: 'three-dots', color: 'muted' }
    };
    return tipos[tipo] || { icon: 'three-dots', color: 'muted' };
  };

  const tipoInfo = getTipoInfo(solicitud.tipoSolicitud);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          <i className={`bi bi-${tipoInfo.icon} text-${tipoInfo.color} me-2`}></i>
          Detalle de Solicitud
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
          </div>
        ) : (
          <Tabs defaultActiveKey="general" className="mb-3">
            
            {/* TAB: Información General */}
            <Tab eventKey="general" title={<><i className="bi bi-info-circle me-2"></i>General</>}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Título</label>
                  <p className="form-control-plaintext">{solicitud.titulo}</p>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-bold">Estado</label>
                  <p className="form-control-plaintext">
                    <span className={`badge bg-${
                      solicitud.estado === 'aprobado' ? 'success' :
                      solicitud.estado === 'rechazado' ? 'danger' :
                      solicitud.estado === 'en_revision' ? 'info' : 'warning'
                    }`}>
                      {solicitud.estado.toUpperCase()}
                    </span>
                  </p>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-bold">Solicitante</label>
                  <p className="form-control-plaintext">
                    <i className="bi bi-person me-2"></i>
                    {solicitud.solicitante.nombre}
                    <br/>
                    <small className="text-muted">{solicitud.solicitante.email}</small>
                  </p>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-bold">Fecha Inicio</label>
                  <p className="form-control-plaintext">
                    <i className="bi bi-calendar3 me-2"></i>
                    {moment.utc(solicitud.fechaInicio).format('DD/MM/YYYY')}
                  </p>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-bold">Fecha Fin</label>
                  <p className="form-control-plaintext">
                    <i className="bi bi-calendar-check me-2"></i>
                    {moment.utc(solicitud.fechaFin).format('DD/MM/YYYY')}
                  </p>
                </div>

                {solicitud.descripcion && (
                  <div className="col-12">
                    <label className="form-label fw-bold">Descripción</label>
                    <p className="form-control-plaintext">{solicitud.descripcion}</p>
                  </div>
                )}

                {/* Campos específicos según tipo */}
                {solicitud.tipoSolicitud === 'capacitacion' && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Nombre Capacitación</label>
                      <p className="form-control-plaintext">{solicitud.nombreCapacitacion}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Institución</label>
                      <p className="form-control-plaintext">{solicitud.institucion}</p>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Modalidad</label>
                      <p className="form-control-plaintext">{solicitud.modalidad}</p>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Duración</label>
                      <p className="form-control-plaintext">{solicitud.duracionHoras} horas</p>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Costo</label>
                      <p className="form-control-plaintext">
                        ${solicitud.costo?.toLocaleString('es-CO')}
                      </p>
                    </div>
                  </>
                )}

                {solicitud.tipoSolicitud === 'permiso' && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Tipo de Permiso</label>
                      <p className="form-control-plaintext">{solicitud.tipoPermiso}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Motivo</label>
                      <p className="form-control-plaintext">{solicitud.motivoPermiso}</p>
                    </div>
                    {solicitud.horaInicio && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Hora Inicio</label>
                          <p className="form-control-plaintext">{solicitud.horaInicio}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Hora Fin</label>
                          <p className="form-control-plaintext">{solicitud.horaFin}</p>
                        </div>
                      </>
                    )}
                  </>
                )}

                {solicitud.tipoSolicitud === 'vacaciones' && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Días Solicitados</label>
                      <p className="form-control-plaintext">{solicitud.diasSolicitados}</p>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Días Disponibles</label>
                      <p className="form-control-plaintext">{solicitud.diasDisponibles}</p>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Periodo</label>
                      <p className="form-control-plaintext">{solicitud.periodoVacacional}</p>
                    </div>
                  </>
                )}

                {solicitud.tipoSolicitud === 'reembolso' && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Valor Solicitado</label>
                      <p className="form-control-plaintext">
                        ${solicitud.valorSolicitado?.toLocaleString('es-CO')}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Categoría</label>
                      <p className="form-control-plaintext">{solicitud.categoria}</p>
                    </div>
                  </>
                )}
              </div>
            </Tab>

            {/* TAB: Flujo de Aprobación */}
            <Tab eventKey="aprobacion" title={<><i className="bi bi-diagram-3 me-2"></i>Flujo de Aprobación</>}>
              <LineaTiempoAprobacion 
                responsables={solicitud.responsablesAprobacion}
                historial={solicitud.historial}
              />
            </Tab>

            {/* TAB: Historial */}
            <Tab eventKey="historial" title={<><i className="bi bi-clock-history me-2"></i>Historial</>}>
              {solicitud.historial && solicitud.historial.length > 0 ? (
                <div className="list-group">
                  {solicitud.historial.map((item, index) => (
                    <div key={index} className="list-group-item">
                      <div className="d-flex w-100 justify-content-between">
                        <h6 className="mb-1">{item.accion}</h6>
                        <small>{moment.utc(item.fecha).format('DD/MM/YYYY HH:mm')}</small>
                      </div>
                      <p className="mb-1">{item.detalles}</p>
                      <small className="text-muted">
                        Por: {item.usuario?.nombre || 'Sistema'}
                      </small>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No hay registros en el historial</p>
              )}
            </Tab>

            {/* TAB: Notas */}
            <Tab eventKey="notas" title={<><i className="bi bi-chat-left-text me-2"></i>Notas ({solicitud.notas?.length || 0})</>}>
              <NotasSolicitud
                solicitud={solicitud}
                usuarioActual={usuarioActual}
                onActualizar={() => { cargarDetalle(); onActualizar?.(); }}
              />
            </Tab>

            {/* TAB: Adjuntos */}
            <Tab eventKey="adjuntos" title={<><i className="bi bi-paperclip me-2"></i>Adjuntos ({solicitud.adjuntos?.length || 0})</>}>
              {solicitud.adjuntos && solicitud.adjuntos.length > 0 ? (
                <div className="list-group">
                  {solicitud.adjuntos.map((adjunto, index) => (
                    <a
                      key={index}
                      href={adjunto.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="list-group-item list-group-item-action"
                    >
                      <div className="d-flex w-100 justify-content-between">
                        <h6 className="mb-1">
                          <i className="bi bi-file-earmark me-2"></i>
                          {adjunto.nombre}
                        </h6>
                        <small>{(adjunto.tamano / 1024).toFixed(2)} KB</small>
                      </div>
                      <small className="text-muted">
                        {moment.utc(adjunto.fechaCarga).format('DD/MM/YYYY HH:mm')}
                      </small>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No hay archivos adjuntos</p>
              )}
            </Tab>
          </Tabs>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onHide}>
          <i className="bi bi-x-lg me-2"></i>
          Cerrar
        </button>

        {puedeEditar() && (
          <button
            className="btn btn-outline-primary"
            onClick={() => {
              onHide?.();
              onEditar?.(solicitud);
            }}
          >
            <i className="bi bi-pencil-square me-2"></i>
            Editar solicitud
          </button>
        )}

        {puedeAprobar() && (
          <>
            <button 
              className="btn btn-danger" 
              onClick={handleRechazar}
              disabled={procesando}
            >
              {procesando ? (
                <span className="spinner-border spinner-border-sm me-2"></span>
              ) : (
                <i className="bi bi-x-lg me-2"></i>
              )}
              Rechazar
            </button>

            <button 
              className="btn btn-success" 
              onClick={handleAprobar}
              disabled={procesando}
            >
              {procesando ? (
                <span className="spinner-border spinner-border-sm me-2"></span>
              ) : (
                <i className="bi bi-check-lg me-2"></i>
              )}
              Aprobar
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ModalDetalleSolicitud;